import type { BoPhanSuDungTSCD, BoPhanSuDungTSCDInput } from '@/types/schemas';
import { DeleteDialog, ErpDataTableV2 } from '@/components/custom/erp';
import { ActionBar, DepartmentDialog } from './components';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function BoPhanSuDungTSCDPage() {
  const {
    data: departments,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<BoPhanSuDungTSCD, BoPhanSuDungTSCDInput>({
    endpoint: QUERY_KEYS.BO_PHAN_SU_DUNG_TSCD,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<BoPhanSuDungTSCD>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : departments || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddDepartment = async (data: BoPhanSuDungTSCDInput) => {
    await addItem(data);
    await refreshData();
  };

  const handleUpdateDepartment = async (data: BoPhanSuDungTSCDInput) => {
    if (selectedObj) {
      await updateItem(selectedObj.uuid, data);
      await refreshData();
    }
  };

  const handleSubmit = async (data: BoPhanSuDungTSCDInput) => {
    try {
      if (formMode === 'add') {
        await handleAddDepartment(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateDepartment(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (err: unknown) {
      console.error('Error submitting form:', err);
    }
  };

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onCopyClick={handleCopyClick}
          onViewClick={handleViewClick}
          isEditDisabled={!selectedObj}
          onRefreshClick={async () => {
            await refreshData();
            if (selectedObj?.uuid) {
              await fetchDetail(selectedObj.uuid);
            }
          }}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
          loading={isLoading}
        />
      </div>

      {showForm && (
        <DepartmentDialog
          open={showForm}
          mode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
          selectedObj={formMode === 'edit' || formMode === 'view' ? selectedObj : undefined}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={async uuid => {
            await deleteItem(uuid);
            await refreshData();
          }}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
